//Imports
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    IconButton,
    MenuComponent,
    SearchInput,
    TableButtons,
    TableView,
    useTablePagination,
} from "@gpstrackit/gtc-react-controls";

//Components
import { Grid, Icon } from "@mui/material";
import { NotificationManager } from "../../../../components/Notifications";
import { Timezone } from 'Components/uiControls';
import Preferences from "Core/containers/Preferences";

//Helpers
import { GetFormatForMoment, completeColumns, splitTextIntoLines } from 'Core/data/Helpers';

//Modals
import MapModal from "Modules/.common/components/MapModal";
import CreateEditLocation from './modals/createEditLocation';
import DeleteLocation from './modals/deleteLocation';

//Actions
import { workOrdersSearchLocations, reset } from "./redux/slice";
import {
    openModalCreateEditLocation,
    openModalDeleteLocation,
    sendGenericExport,
    savePreference,
    clearRedux
} from "Redux/actions";

//Variables
const KEY_PREFERENCE = "WOLocationTab-list";

const LocationTab = ({ customerId, fullHeight }) => {

    //States
    const [selectedItems, setSelectedItems] = useState([]);
    const [preferences, setPreferences] = useState({});
    const [dataToMap, setDataToMap] = useState({});

    //Table refs
    const locationTableRef = useRef();

    //Language
    const { messages, locale } = useIntl();

    //Redux States
    const { loading, locations, total, error } = useSelector((state) => state.workOrdersLocationTabRedux);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);
    const user = useSelector((state) => state.securityRedux.user);
    const openModalDelete = useSelector((state) => state.workOrdersDeleteLocationRedux.open);
    const openModalCreateEdit = useSelector((state) => state.workOrdersCreateEditLocationRedux.open);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);

    const { dateformat, timeZone } = user;

    const format = GetFormatForMoment(dateformat);

    const dispatch = useDispatch();
    const paginationLocations = useTablePagination({
        dispatch,
        action: workOrdersSearchLocations,
        searchFields: ["id", "name"],
        tableRef: locationTableRef,
        sendGenericExport,
        exportName: "locations",
        termsFields: [{
            fields: "customerId",
            terms: [+customerId],
            exact: true
        }]
    });

    //Options
    const getCustomerMenuOptions = () => [
        messages["edit"],
        messages["delete"]
    ];

    //Handlers and utils
    const handleClickWorkOrderMenu = (type, location) => {
        if (type === messages["edit"]) {
            dispatch(openModalCreateEditLocation({ customerId: +customerId, locationId: location?.id }))
        } else if (type === messages["delete"]) {
            if (location?.locationType === "main") {
                NotificationManager.error(
                    messages['workOrder_locationTab_warningDeleteLocation'],
                    messages['workOrder_locationTab_deleteLocations'],
                    3000
                );
            } else {
                dispatch(openModalDeleteLocation({ location: location }))
            }
        }
    };

    const refreshTable = () => {
        setSelectedItems([]);
        locationTableRef?.current?.setPage(1);
        locationTableRef?.current?.setSelectedItems([]);
        paginationLocations.onChangePage(1);
    };

    const savePreferences = (key, value) => {
        let preferenceValue = { ...preferences, [key]: value }
        setPreferences(preferenceValue)
        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: KEY_PREFERENCE, preferenceValue: preferenceValue }));
    }

    //Columns
    let currentColumns = [
        {
            key: "name",
            title: messages['workOrder_locationTab_name'],
            functionString: `
                function splitTextIntoLines(text, maxLength) {
                    let result = "";
                    for (let i = 0; i < text.length; i += maxLength) {
                        result += text.slice(i, i + maxLength) + "\\n";
                    }
                    return result.trim();
                };
                return row?.name ? splitTextIntoLines(row?.name, 35) : "";
            `,
            sort: true,
            sortField: "name",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.name ? splitTextIntoLines(row?.name, 35) : "",
            render: (row) => (
                <Grid container alignItems="center">
                    <Grid item xs={10} style={{ wordWrap: 'break-word', width: 200 }}>
                        <span>{row?.name}</span>
                    </Grid>
                    <Grid item xs={2}>
                        <MenuComponent
                            placement="right-start"
                            options={getCustomerMenuOptions(row?.unit?.label)}
                            icon="more_vert"
                            className="showInHover"
                            onClick={handleClickWorkOrderMenu}
                            value={row}
                        />
                    </Grid>
                </Grid>
            )
        },
        {
            key: "locationType",
            title: messages['workOrder_locationTab_type'],
            functionString: `
                let lang = "${locale}"
                const translations = {
                    en: {
                        main: "Main",
                        billing: "Billing",
                        regular: "Regular"
                    },
                    es: {
                        main: "Principal",
                        billing: "Facturación",
                        regular: "Regular"
                    }
                };

                const locationType = row?.locationType;
                const type = translations[lang]?.[locationType] || locationType;

                return type;
            `,
            sort: true,
            sortField: "locationType",
            show: true,
            export: true,
            width: 150,
            exportValue: (row) => row?.locationType ? messages[`workOrder_locationTab_${row?.locationType}`] || row?.locationType : "",
            render: (row) => <span>{messages[`workOrder_locationTab_${row?.locationType}`] || row?.locationType}</span>
        },
        {
            key: "location",
            title: messages['workOrder_locationTab_address'],
            functionString: `
                function splitTextIntoLines(text, maxLength) {
                    let result = "";
                    for (let i = 0; i < text.length; i += maxLength) {
                        result += text.slice(i, i + maxLength) + "\\n";
                    }
                    return result.trim();
                };
                let currentAddress = ""
                try {
                    currentAddress = JSON.parse(row?.address)
                } catch (error) { }
                return splitTextIntoLines(currentAddress?.address, 35) || ""
            `,
            show: true,
            export: true,
            width: 400,
            exportValue: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.address)
                } catch (error) { }
                return currentAddress?.address ? splitTextIntoLines(currentAddress?.address, 35) : ""
            },
            render: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.address)
                } catch (error) { }

                return (
                    <Grid container alignItems="center">
                        {currentAddress?.address && (
                            <Grid
                                className="trackerTab-location-address"
                                item
                                onClick={() => {
                                    const data = {
                                        show: true,
                                        title: currentAddress.address,
                                        isPointerMarker: true,
                                        pointers: [
                                            {
                                                latitude: row?.latitude,
                                                longitude: row?.longitude,
                                                label: "Map Point",
                                                heading: 0,
                                                color: "000",
                                                eventName: "no event",
                                                draggable: false
                                            }
                                        ]
                                    };
                                    setDataToMap(data);
                                }}
                            >
                                <Icon className="iconFlag">room</Icon>
                                <p>{currentAddress.address || ""}</p>
                            </Grid>
                        )}
                    </Grid>
                )
            }
        },
        {
            key: "contact",
            title: messages['workOrder_locationTab_description'],
            functionString: `
                function splitTextIntoLines(text, maxLength) {
                    let result = "";
                    for (let i = 0; i < text.length; i += maxLength) {
                        result += text.slice(i, i + maxLength) + "\\n";
                    }
                    return result.trim();
                };
                return row?.contact ? splitTextIntoLines(row?.contact, 35) : "";
            `,
            show: true,
            export: true,
            width: 300,
            exportValue: (row) => row?.contact ? splitTextIntoLines(row?.contact, 35) : "",
            render: (row) => (
                <Grid container alignItems="center">
                    <Grid item style={{ wordWrap: 'break-word', width: 300 }}>
                        <span>{row?.contact}</span>
                    </Grid>
                </Grid>
            )
        },
        {
            key: "createdOn",
            title: messages['workOrder_locationTab_createdOn'],
            functionString: `
                function formatDate(date,tz,formatDate) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format(formatDate)
                    return formatted;
                }

                let timeZone = "${timeZone}"
                let format = "${format}" + " hh:mm a, z"

                return row?.createdOn ? formatDate(row?.createdOn,timeZone,format) : ''
            `,
            sort: true,
            sortField: "createdOn",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.createdOn,
            render: (row) => (
                <Timezone
                    date={row?.createdOn}
                    dateFormat={dateformat}
                    timeFormat="hh:mm a"
                    timezone={timeZone}
                />
            )
        }
    ]

    useEffect(() => {
        paginationLocations.setColumns(currentColumns);
        setSelectedItems([]);
        locationTableRef?.current?.setPage(1);
        locationTableRef?.current?.setSelectedItems([]);
    }, []);

    useEffect(() => {
        paginationLocations.refresh({ _sort: { key: "id", order: "asc" } });
        return () => dispatch(reset());
    }, [])

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                messages["errorMessage"],
                messages["error"],
                3000,
                null,
                null,
                "warning"
            );
            dispatch(reset({ error: null }));
        }
        if (exportResult && exportName === "locations") {
            NotificationManager.info(
                messages["exportInProcessBody"],
                messages["exportInProcessTitle"],
                4000,
                null,
                null,
                "info",
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [exportResult, exportName, error]);

    useEffect(() => {
        if (preferences?.columns?.length) {
            const preferenceMap = new Map(preferences.columns.map(col => [col.key, col]));
            // Reorder columns based on preferences (keeping `show`)
            const reorderedColumns = preferences.columns
                .map(pref => {
                    const col = currentColumns.find(c => c.key === pref.key);
                    return col ? { ...col, show: pref.show } : null;
                })
                .filter(Boolean);
            // Add new columns (not listed in preferences), with `show: true` by default
            const newColumns = currentColumns
                .filter(col => !preferenceMap.has(col.key))
                .map(col => ({ ...col, show: true }));

            const finalColumns = [...reorderedColumns, ...newColumns];
            paginationLocations.setColumns(finalColumns);
            setPreferences({ ...preferences, columns: [] });
        }
    }, [preferences.columns])

    useEffect(() => {
        if (paginationLocations.columns.length > 0) {
            setPreferences({ ...preferences, columns: paginationLocations.columns })
        }
    }, [locale, timeZone, dateformat]);

    return (
        <Fragment>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                
            >
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            <IconButton
                                tooltip={messages["add"]}
                                placement="bottom"
                                variant="secondary"
                                icon="add_box"
                                size="small"
                                disabled={true || loading}
                                onClick={() => {
                                    dispatch(openModalCreateEditLocation({ customerId: +customerId }))
                                }}
                            />
                            {
                                selectedItems?.length ? (
                                    <IconButton
                                        tooltip={messages["delete"]}
                                        placement="bottom"
                                        variant="secondary"
                                        icon="delete"
                                        size="small"
                                        onClick={() => {
                                            let mainLocation = selectedItems?.some((item) => item?.locationType === "main")
                                            if (mainLocation) {
                                                NotificationManager.error(
                                                    messages['workOrder_locationTab_warningDeleteLocation'],
                                                    messages['workOrder_locationTab_deleteLocations'],
                                                    3000
                                                );
                                            } else {
                                                dispatch(openModalDeleteLocation({ locationList: selectedItems }))
                                            }
                                        }}
                                    />
                                ) : <></>
                            }
                            <TableButtons
                                messages={messages}
                                {...paginationLocations}
                                buttons={["export", "orderColumn"]}
                                onExport={(type) => {
                                    paginationLocations.onExport(type);
                                }}
                                onChangeColumns={(columns) => {
                                    savePreferences("columns", columns)
                                    paginationLocations.setColumns(columns)
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages["search"]}
                        searchText={paginationLocations.searchText}
                        onSearchText={paginationLocations.onSearchChange}
                    />
                </Grid>
            </Grid>
            <Preferences
                preferencesKey={KEY_PREFERENCE}
                onInitialPreferences={(data) => {
                    setPreferences(data)
                }}
            >
                <TableView
                    keyColumn="id"
                    loading={loading}
                    topheight={fullHeight ? 280 : 340}
                    data={locations || []}
                    total={total}
                    exportOrientation="landscape"
                    onSelectedItems={(items) => setSelectedItems(items)}
                    {...paginationLocations}
                    onChangeColumns={(columns) => {
                        let currentColumns = paginationLocations?.columns || []
                        let finalsColumns = completeColumns(currentColumns, columns)
                        savePreferences("columns", finalsColumns)
                        paginationLocations.setColumns(finalsColumns)
                    }}
                    hideAllInPagination
                />
            </Preferences>
            {openModalCreateEdit && <CreateEditLocation refresh={refreshTable} />}
            {openModalDelete && <DeleteLocation refresh={refreshTable} />}
            {
                dataToMap?.show &&
                <MapModal
                    hiddenButton
                    {...dataToMap}
                    hiddenTraffic={true}
                    hiddenWeather={true}
                    hiddenSearchMap={true}
                    hideModal={() => {
                        setDataToMap({ show: false });
                    }}
                    zoom={18}
                />
            }
        </Fragment>
    );
};

export default LocationTab;
